// ============================================
// FAQ 留言管理台（/faq/uadmin/questions）
// ============================================

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Clock3, Link2, Loader2, MailCheck, MailWarning,
  MessageSquare, Paperclip, Plus, RefreshCw, Search, Send, XCircle,
} from 'lucide-react';
import FaqDiscussionContent from './FaqDiscussionContent';
import FaqEditorModal from './FaqEditorModal';
import FaqRichEditor from './FaqRichEditor';
import { useFaqStore } from './faq-store';
import { useFaqQuestionStore } from './faq-question-store';
import { uploadFaqImage } from './faq-service';
import { EMPTY_TIPTAP_DOC, richTextHasContent, textToTiptapDoc, tiptapToPlainText } from './faq-rich-content';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqItem, FaqQuestion, FaqQuestionStatus } from './types';

const statusConfig: Record<FaqQuestionStatus, { label: string; icon: typeof Clock3; className: string }> = {
  open: { label: '待回复', icon: Clock3, className: 'bg-amber-50 text-amber-600 border-amber-100' },
  answered: { label: '已回复', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  closed: { label: '已关闭', icon: XCircle, className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

type DetailSection = 'content' | 'thread' | 'reply';

function fmtTime(value: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function FaqQuestionAdminPage() {
  const { space, path } = useFaqSpace();
  const {
    questions,
    currentQuestion,
    loading,
    detailLoading,
    saving,
    error,
    fetchQuestions,
    fetchQuestionDetail,
    addReply,
    updateQuestionStatus,
    sendReplyEmail,
  } = useFaqQuestionStore();
  const { items, fetchItems, fetchTags } = useFaqStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<FaqQuestionStatus | 'all'>('open');
  const [replyBody, setReplyBody] = useState(EMPTY_TIPTAP_DOC);
  const [replyEditorKey, setReplyEditorKey] = useState(0);
  const [faqQuery, setFaqQuery] = useState('');
  const [selectedFaqIds, setSelectedFaqIds] = useState<string[]>([]);
  const [faqPickerOpen, setFaqPickerOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionHint, setActionHint] = useState('');
  const [activeSection, setActiveSection] = useState<DetailSection | null>('content');
  const requestedQuestionId = searchParams.get('questionId') || searchParams.get('question');
  const requestedSectionParam = searchParams.get('section');
  const requestedSection: DetailSection | null =
    requestedSectionParam === 'content' || requestedSectionParam === 'thread' || requestedSectionParam === 'reply'
      ? requestedSectionParam
      : null;
  const uploadImage = (file: File) => uploadFaqImage(file, space.slug);

  useEffect(() => {
    fetchQuestions();
    fetchItems();
    fetchTags();
  }, [fetchQuestions, fetchItems, fetchTags]);

  useEffect(() => {
    if (questions.length === 0) return;
    if (requestedQuestionId) {
      const requestedQuestion = questions.find((q) => q.id === requestedQuestionId);
      if (requestedQuestion) {
        if (status !== requestedQuestion.status) {
          setStatus(requestedQuestion.status);
        }
        if (selectedId !== requestedQuestion.id) {
          setSelectedId(requestedQuestion.id);
        }
        return;
      }
    }
    if (!selectedId && questions.length > 0) {
      const firstOpen = questions.find((q) => q.status === 'open') || questions[0];
      setSelectedId(firstOpen.id);
    }
  }, [questions, requestedQuestionId, selectedId, status]);

  useEffect(() => {
    if (selectedId) {
      fetchQuestionDetail(selectedId);
      setReplyBody(EMPTY_TIPTAP_DOC);
      setReplyEditorKey((key) => key + 1);
      setSelectedFaqIds([]);
      setFaqPickerOpen(false);
      setActiveSection(requestedSection ?? 'content');
      setActionError('');
      setActionHint('');
    }
  }, [selectedId, requestedSection, fetchQuestionDetail]);

  const filteredQuestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return questions.filter((item) => {
      const statusMatched = status === 'all' || item.status === status;
      const textMatched = !q ||
        item.title.toLowerCase().includes(q) ||
        tiptapToPlainText(item.content).toLowerCase().includes(q) ||
        item.author?.email?.toLowerCase().includes(q) ||
        item.author?.name?.toLowerCase().includes(q);
      return statusMatched && textMatched;
    });
  }, [questions, query, status]);

  const counts = useMemo(() => ({
    all: questions.length,
    open: questions.filter((q) => q.status === 'open').length,
    answered: questions.filter((q) => q.status === 'answered').length,
    closed: questions.filter((q) => q.status === 'closed').length,
  }), [questions]);

  const selectableFaqs = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    return items
      .filter((item) => item.is_published && !item.deleted_at)
      .filter((item) => !q || item.question.toLowerCase().includes(q))
      .slice(0, 30);
  }, [items, faqQuery]);

  const initialDraft = useMemo(() => currentQuestion
    ? {
        question: currentQuestion.title,
        answer: textToTiptapDoc(`用户原始留言：\n${tiptapToPlainText(currentQuestion.content)}`),
      }
    : undefined, [currentQuestion]);

  const selectedFaqs = useMemo(() => selectedFaqIds
    .map((id) => items.find((item) => item.id === id))
    .filter(Boolean) as FaqItem[], [items, selectedFaqIds]);

  const canReply = !!currentQuestion && currentQuestion.status !== 'closed' && richTextHasContent(replyBody);

  function toggleSection(section: DetailSection) {
    setActiveSection((current) => current === section ? null : section);
  }

  async function handleReply() {
    if (!currentQuestion || !richTextHasContent(replyBody)) return;
    setActionError('');
    setActionHint('');
    try {
      const reply = await addReply({
        questionId: currentQuestion.id,
        body: replyBody,
        bodyFormat: 'tiptap',
        isAdminReply: true,
        linkedFaqIds: selectedFaqIds,
      });
      setReplyBody(EMPTY_TIPTAP_DOC);
      setReplyEditorKey((key) => key + 1);
      setSelectedFaqIds([]);
      setFaqPickerOpen(false);
      setEmailSending(true);
      try {
        await sendReplyEmail(reply.id);
        setActionHint('回复已保存，邮件已发送给提问人');
      } catch (error) {
        setActionError(`回复已保存，但邮件发送失败：${getErrorMessage(error, '未知错误')}`);
      } finally {
        setEmailSending(false);
      }
    } catch (error) {
      setActionError(getErrorMessage(error, '回复失败'));
    }
  }

  async function handleStatus(nextStatus: FaqQuestionStatus) {
    if (!currentQuestion) return;
    setActionError('');
    try {
      await updateQuestionStatus(currentQuestion.id, nextStatus);
    } catch (error) {
      setActionError(getErrorMessage(error, '状态更新失败'));
    }
  }

  function toggleFaq(id: string) {
    setSelectedFaqIds((prev) => prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]);
  }

  function handleCreatedFaq(item: FaqItem) {
    setSelectedFaqIds((prev) => prev.includes(item.id) ? prev : [...prev, item.id]);
    setFaqPickerOpen(true);
    setEditorOpen(false);
    fetchItems();
    setActionHint(`已创建 FAQ「${item.question}」，发送回复时会自动附上`);
  }

  return (
    <div className="flex h-[calc(100vh-56px)] bg-slate-50">
      <aside className="flex w-[360px] shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-slate-800">留言管理</h1>
              <p className="mt-1 text-xs text-slate-400">处理员工提问并附上 FAQ</p>
            </div>
            <button
              onClick={() => fetchQuestions()}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              title="刷新"
            >
              <RefreshCw size={16} />
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索留言..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
          </div>
          <div className="mt-3 flex items-center gap-1 overflow-x-auto">
            {[
              { key: 'open' as const, label: '待回复', count: counts.open },
              { key: 'answered' as const, label: '已回复', count: counts.answered },
              { key: 'closed' as const, label: '已关闭', count: counts.closed },
              { key: 'all' as const, label: '全部', count: counts.all },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatus(tab.key)}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  status === tab.key ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {tab.label} {tab.count}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {loading && (
            <div className="flex justify-center py-10">
              <Loader2 size={24} className="animate-spin text-sky-500" />
            </div>
          )}
          {!loading && filteredQuestions.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">
              暂无匹配留言
            </div>
          )}
          {!loading && filteredQuestions.map((item: FaqQuestion) => {
            const config = statusConfig[item.status];
            const Icon = config.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setSelectedId(item.id);
                  if (searchParams.toString()) {
                    setSearchParams({});
                  }
                }}
                className={`mb-2 w-full rounded-xl border px-3 py-3 text-left transition ${
                  selectedId === item.id
                    ? 'border-sky-200 bg-sky-50/60'
                    : 'border-slate-200 bg-white hover:border-sky-100 hover:bg-slate-50'
                }`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-semibold ${config.className}`}>
                    <Icon size={12} />
                    {config.label}
                  </span>
                  <span className="text-[11px] text-slate-400">{fmtTime(item.last_reply_at || item.created_at)}</span>
                </div>
                <p className="line-clamp-2 text-sm font-semibold text-slate-700">{item.title}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{tiptapToPlainText(item.content)}</p>
                <p className="mt-2 text-[11px] text-slate-400">{item.author?.name || item.author?.email || '员工'}</p>
              </button>
            );
          })}
        </div>
      </aside>

      <main className="min-h-0 min-w-0 flex-1 overflow-hidden bg-white">
        {detailLoading && (
          <div className="flex h-full items-center justify-center">
            <Loader2 size={28} className="animate-spin text-sky-500" />
          </div>
        )}

        {!detailLoading && !currentQuestion && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-slate-400">
              <MessageSquare size={42} className="mx-auto mb-3 text-slate-300" />
              <p>{error || '请选择一条留言'}</p>
            </div>
          </div>
        )}

        {!detailLoading && currentQuestion && (
          <div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50">
            <section className="shrink-0 border-b border-slate-200 bg-white">
              <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {(() => {
                      const config = statusConfig[currentQuestion.status];
                      const Icon = config.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${config.className}`}>
                          <Icon size={13} />
                          {config.label}
                        </span>
                      );
                    })()}
                    <span className="text-xs text-slate-400">提问人：{currentQuestion.author?.name || currentQuestion.author?.email || '员工'}</span>
                  </div>
                  <h2 className="truncate text-xl font-bold text-slate-800">{currentQuestion.title}</h2>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {currentQuestion.status !== 'closed' ? (
                    <button
                      onClick={() => handleStatus('closed')}
                      className="rounded-md bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:bg-slate-200"
                    >
                      关闭
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStatus('open')}
                      className="rounded-md bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-600 transition hover:bg-amber-100"
                    >
                      重新打开
                    </button>
                  )}
                </div>
              </div>
            </section>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden bg-slate-50 p-3">
              <section className={`${activeSection === 'content' ? 'min-h-0 flex flex-1 flex-col' : 'shrink-0'} overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm`}>
                <button
                  type="button"
                  onClick={() => toggleSection('content')}
                  className="group flex h-12 shrink-0 items-center justify-between gap-3 px-5 text-left transition hover:bg-slate-50"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {activeSection === 'content' ? <ChevronUp size={15} className="shrink-0 text-slate-300 group-hover:text-sky-500" /> : <ChevronDown size={15} className="shrink-0 text-slate-300 group-hover:text-sky-500" />}
                    <h3 className="shrink-0 text-sm font-bold text-slate-800">留言内容</h3>
                    <span className="truncate text-xs text-slate-400">
                      {tiptapToPlainText(currentQuestion.content).slice(0, 72) || '暂无内容'}
                    </span>
                  </div>
                  <span className="shrink-0 text-[11px] font-semibold text-slate-400">
                    {activeSection === 'content' ? '收起' : '展开'}
                  </span>
                </button>

                {activeSection === 'content' && (
                  <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-100 px-8 py-5">
                    <FaqDiscussionContent
                      content={currentQuestion.content}
                      format={currentQuestion.content_format}
                    />
                    {currentQuestion.related_faq && (
                      <a
                        href={path(currentQuestion.related_faq.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 block border-l-2 border-sky-300 bg-sky-50/50 px-3 py-2 transition hover:bg-sky-50"
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-500">关联 FAQ</p>
                        <p className="mt-1 text-sm font-semibold text-slate-700">{currentQuestion.related_faq.question}</p>
                      </a>
                    )}
                  </div>
                )}
              </section>

              <section className={`${activeSection === 'thread' ? 'min-h-0 flex flex-1 flex-col' : 'shrink-0'} overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm`}>
                <button
                  type="button"
                  onClick={() => toggleSection('thread')}
                  className="group flex h-12 shrink-0 items-center justify-between gap-3 px-5 text-left transition hover:bg-slate-50"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {activeSection === 'thread' ? <ChevronUp size={15} className="shrink-0 text-slate-300 group-hover:text-sky-500" /> : <ChevronDown size={15} className="shrink-0 text-slate-300 group-hover:text-sky-500" />}
                    <h3 className="shrink-0 text-sm font-bold text-slate-800">沟通记录</h3>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                      {currentQuestion.replies.length} 条
                    </span>
                    <span className="truncate text-xs text-slate-400">
                      {currentQuestion.replies.length > 0 ? '按时间保留员工补充和管理员回复' : '暂无回复'}
                    </span>
                  </div>
                  <span className="shrink-0 text-[11px] font-semibold text-slate-400">
                    {activeSection === 'thread' ? '收起' : '展开'}
                  </span>
                </button>

                {activeSection === 'thread' && (
                  <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-100">
                    {currentQuestion.replies.length === 0 ? (
                      <div className="flex h-full min-h-[120px] items-center justify-center text-sm text-slate-400">
                        暂无回复
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {currentQuestion.replies.map((reply) => (
                          <div key={reply.id} className={`px-6 py-4 ${
                            reply.is_admin_reply ? 'bg-emerald-50/30' : 'bg-white'
                          }`}>
                            <div className="mb-2 flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-700">
                                {reply.author?.name || reply.author?.email || '用户'}
                                {reply.is_admin_reply && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-600">管理员</span>}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-slate-400">
                                {reply.is_admin_reply && reply.email_status === 'sent' && <span className="inline-flex items-center gap-1 text-emerald-600"><MailCheck size={13} /> 已通知</span>}
                                {reply.is_admin_reply && reply.email_status === 'failed' && <span className="inline-flex items-center gap-1 text-red-500"><MailWarning size={13} /> 邮件失败</span>}
                                <span>{fmtTime(reply.created_at)}</span>
                              </div>
                            </div>
                            <FaqDiscussionContent content={reply.body} format={reply.body_format} />
                            {reply.email_error && <p className="mt-2 text-xs text-red-500">{reply.email_error}</p>}
                            {reply.links && reply.links.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {reply.links.map((link) => link.faq && (
                                  <a
                                    key={link.id}
                                    href={path(link.faq.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded-md bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-600 ring-1 ring-sky-100 hover:bg-sky-100"
                                  >
                                    <Link2 size={12} />
                                    {link.faq.question}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className={`${activeSection === 'reply' ? 'min-h-0 flex flex-1 flex-col' : 'shrink-0'} overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm`}>
                <div className="flex h-12 shrink-0 flex-wrap items-center justify-between gap-3 px-5">
                  <button
                    type="button"
                    onClick={() => toggleSection('reply')}
                    className="group flex min-w-0 items-center gap-2 text-left"
                  >
                    {activeSection === 'reply' ? <ChevronUp size={15} className="shrink-0 text-slate-300 group-hover:text-sky-500" /> : <ChevronDown size={15} className="shrink-0 text-slate-300 group-hover:text-sky-500" />}
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold text-slate-800">回复留言</h3>
                      <p className="mt-0.5 truncate text-xs text-slate-400">
                        {richTextHasContent(replyBody) ? '草稿未发送' : '回复保存后邮件通知提问人，可按需附上知识问答。'}
                      </p>
                    </div>
                  </button>
                  {activeSection === 'reply' && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setFaqPickerOpen((open) => !open)}
                        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
                          faqPickerOpen || selectedFaqIds.length > 0
                            ? 'border-sky-100 bg-sky-50 text-sky-600 hover:bg-sky-100'
                            : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        <Paperclip size={14} />
                        附上 FAQ
                        <span className="rounded-full bg-white/80 px-1.5 py-0.5 text-[10px]">{selectedFaqIds.length}</span>
                        {faqPickerOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                      >
                        <Plus size={14} />
                        从留言生成 FAQ
                      </button>
                    </div>
                  )}
                </div>

                {activeSection === 'reply' && (
                  <>
                    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                      <div className={`border px-3.5 py-3 transition focus-within:border-sky-300 focus-within:ring-2 focus-within:ring-sky-100 ${
                        currentQuestion.status === 'closed'
                          ? 'border-slate-200 bg-slate-100/70 opacity-70'
                          : 'border-slate-200 bg-slate-50'
                      }`}>
                        <FaqRichEditor
                          key={replyEditorKey}
                          content={replyBody}
                          onChange={setReplyBody}
                          placeholder={currentQuestion.status === 'closed' ? '留言已关闭，重新打开后才能回复' : '输入管理员回复，可上传截图、插入链接或表格...'}
                          uploadImage={uploadImage}
                          minHeight="112px"
                          readOnly={currentQuestion.status === 'closed'}
                        />
                      </div>

                      {faqPickerOpen && (
                        <div className="mt-3 border border-slate-200 bg-slate-50/70 p-3">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-bold text-slate-600">附上 FAQ</p>
                              <p className="mt-0.5 text-[11px] text-slate-400">选中的 FAQ 会随回复一起展示给员工</p>
                            </div>
                            <p className="text-[11px] text-slate-400">已选 {selectedFaqIds.length} 条</p>
                          </div>
                          <div className="relative mb-2">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              value={faqQuery}
                              onChange={(e) => setFaqQuery(e.target.value)}
                              placeholder="搜索已发布 FAQ..."
                              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                            />
                          </div>
                          <div className="max-h-36 space-y-1 overflow-y-auto pr-1">
                            {selectableFaqs.length === 0 && (
                              <div className="border border-dashed border-slate-200 bg-white/60 py-5 text-center text-xs text-slate-400">
                                暂无匹配 FAQ
                              </div>
                            )}
                            {selectableFaqs.map((item) => (
                              <label key={item.id} className="flex cursor-pointer items-start gap-2 px-2 py-2 text-xs transition hover:bg-white">
                                <input
                                  type="checkbox"
                                  checked={selectedFaqIds.includes(item.id)}
                                  onChange={() => toggleFaq(item.id)}
                                  className="mt-0.5"
                                />
                                <span className="line-clamp-2 text-slate-600">{item.question}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {!faqPickerOpen && selectedFaqs.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedFaqs.slice(0, 3).map((item) => (
                            <button
                              type="button"
                              key={item.id}
                              onClick={() => toggleFaq(item.id)}
                              className="inline-flex max-w-[260px] items-center gap-1.5 rounded-md border border-sky-100 bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-600 transition hover:bg-sky-100"
                              title="点击移除"
                            >
                              <Link2 size={12} />
                              <span className="truncate">{item.question}</span>
                              <XCircle size={12} />
                            </button>
                          ))}
                          {selectedFaqs.length > 3 && (
                            <span className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-500">
                              +{selectedFaqs.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-col gap-3 border-t border-slate-100 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 text-xs">
                        {actionError ? (
                          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-600">
                            <AlertCircle size={14} className="shrink-0" />
                            <span className="line-clamp-2">{actionError}</span>
                          </div>
                        ) : actionHint ? (
                          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-600">
                            {actionHint}
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2 text-slate-400">
                            <span className="inline-flex items-center gap-1.5">
                              <MailCheck size={14} />
                              邮件将只通知提问人
                            </span>
                            {currentQuestion.status === 'closed' && (
                              <span className="rounded-lg bg-slate-100 px-2 py-1 text-slate-500">留言已关闭</span>
                            )}
                            {selectedFaqIds.length > 0 && (
                              <span className="rounded-lg bg-sky-50 px-2 py-1 text-sky-600">已附 {selectedFaqIds.length} 条 FAQ</span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleReply}
                        disabled={saving || emailSending || !canReply}
                        className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-md bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:bg-slate-200 disabled:text-slate-400 sm:w-auto"
                      >
                        {saving || emailSending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        {emailSending ? '发送邮件中...' : '回复并通知'}
                      </button>
                    </div>
                  </>
                )}
            </section>
            </div>
          </div>
        )}
      </main>

      <FaqEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        initialDraft={initialDraft}
        onSaved={handleCreatedFaq}
      />
    </div>
  );
}
